import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { RefObject } from 'react'
import { message } from 'antd'
import type { AgGridReact } from '@ag-grid-community/react'

export interface UsePaginatedSelectionParams<TRow> {
  gridRef: RefObject<AgGridReact<TRow>>
  /** Rows of the CURRENT server page — ag-grid only ever knows about these. */
  rows: TRow[]
  /** The full selection, which may include rows from pages not currently loaded. */
  selectedRows: TRow[]
  /** Stable identity of a row. Must be referentially stable across renders. */
  keyOf: (row: TRow) => string
  maxSelection: number
  onSelectionChange: (rows: TRow[]) => void
}

export interface PaginatedSelection {
  /** Wire straight to `AgGridReact.onSelectionChanged`. */
  onSelectionChanged: () => void
}

export interface ReconcilePageSelectionInput<TRow> {
  rows: TRow[]
  selectedRows: TRow[]
  keyOf: (row: TRow) => string
  pageKey?: string
  prevPageKey?: string
  prevRowKeys: Set<string>
}

export interface ReconcilePageSelectionResult<TRow> {
  /** `null` when the selection is already current. */
  selectedRows: TRow[] | null
  pageKey: string | undefined
  rowKeys: Set<string>
}

function hasSameKeys(current: string[], next: string[]): boolean {
  if (current.length !== next.length) return false
  const set = new Set(current)
  if (set.size !== current.length) return false
  return next.every((key) => set.has(key))
}

function indexRows<TRow>(
  rows: TRow[],
  keyOf: (row: TRow) => string
): Map<string, TRow> {
  const rowByKey = new Map<string, TRow>()
  for (const row of rows) {
    rowByKey.set(keyOf(row), row)
  }
  return rowByKey
}

/**
 * Overlay the current page's rows onto the selection. Keys that left this page
 * (`dropKeys`) are removed; keys still on the page take the fresh row object so
 * callers don't keep acting on a pre-refetch snapshot. Other-page keys are kept.
 * Returns `null` when nothing changed, so the caller can skip the parent update.
 */
function reconcileSelectedRows<TRow>(
  selectedRows: TRow[],
  rowByKey: Map<string, TRow>,
  keyOf: (row: TRow) => string,
  dropKeys?: Set<string>
): TRow[] | null {
  let changed = false
  const next: TRow[] = []
  for (const row of selectedRows) {
    const key = keyOf(row)
    if (dropKeys?.has(key)) {
      changed = true
      continue
    }
    const fresh = rowByKey.get(key)
    if (fresh !== undefined && fresh !== row) {
      changed = true
      next.push(fresh)
    } else {
      next.push(row)
    }
  }
  return changed ? next : null
}

/**
 * Keeps a row selection alive across pagination — page 1's ticks survive a trip
 * to page 2 and back. Scoped entirely to one paginated grid: the hook holds no
 * state of its own, so two grids using it share nothing.
 *
 * The grid's `rowData` is one page, so anything it does not currently hold is,
 * to it, deselected. Selection therefore lives in React state and the grid is
 * treated as a view of it: on every grid change the page's selection is merged
 * back over the rows belonging to other pages, and whenever the state or the
 * page changes the grid's checkboxes are re-applied from the state.
 *
 * The cap is enforced here rather than at action time because a silent trim at
 * submit would terminate a different set than the admin ticked. Rows already
 * selected on other pages win: they are not visible to un-tick, so trimming
 * them would be invisible destruction.
 */
export function usePaginatedSelection<TRow>({
  gridRef,
  rows,
  selectedRows,
  keyOf,
  maxSelection,
  onSelectionChange,
}: UsePaginatedSelectionParams<TRow>): PaginatedSelection {
  const selectedKeys = useMemo(
    () => selectedRows.map(keyOf),
    [selectedRows, keyOf]
  )

  /**
   * True while the effect below is writing the selection into the grid.
   *
   * ag-grid fires `selectionChanged` on every `setSelected`, including ours —
   * the second argument is `clearSelection`, not a suppress flag, and no
   * suppress flag exists on that API. Without this the handler runs partway
   * through the re-apply, sees a half-ticked page and commits it: paging back
   * to a page with three ticked cards left one selected, and Terminate then
   * acted on fewer cards than the admin had chosen.
   */
  const isApplyingRef = useRef(false)

  // Re-apply state onto the grid. Depends on `rows` as well as the selection:
  // a new page arrives with every checkbox cleared, and the rows it shares with
  // the selection have to be re-ticked.
  useEffect(() => {
    const api = gridRef.current?.api
    if (!api) return
    const selected = new Set(selectedKeys)
    isApplyingRef.current = true
    try {
      api.forEachNode((node) => {
        if (!node.data) return
        const shouldSelect = selected.has(keyOf(node.data))
        if (node.isSelected() !== shouldSelect) {
          node.setSelected(shouldSelect, false)
        }
      })
    } finally {
      isApplyingRef.current = false
    }
  }, [gridRef, keyOf, selectedKeys, rows])

  const onSelectionChanged = useCallback(() => {
    // Our own write, not a click. The grid is being brought in line with state
    // we already hold, so there is nothing to report back.
    if (isApplyingRef.current) return
    const api = gridRef.current?.api
    const selectedOnPage = api?.getSelectedRows() ?? []
    const currentPageKeys = new Set(rows.map(keyOf))
    const otherPagesRows = selectedRows.filter(
      (row) => !currentPageKeys.has(keyOf(row))
    )
    const merged = [...otherPagesRows, ...selectedOnPage]

    if (merged.length > maxSelection) {
      // Held rows are clamped too, though only a caller seeding the selection
      // past the cap can put us here: emitting an over-cap selection would be
      // rejected wholesale by the endpoint's ArrayMaxSize, which is a worse
      // failure than trimming.
      const heldRows = otherPagesRows.slice(0, maxSelection)
      const available = Math.max(0, maxSelection - heldRows.length)
      const trimmedPage = selectedOnPage.slice(0, available)
      const allowed = new Set(trimmedPage.map(keyOf))
      // Same guard as the re-apply effect, for the same reason: these are our
      // writes. ag-grid fires selectionChanged on each one, so without it a
      // header select-all over the cap re-enters this handler once per trimmed
      // row — a warning toast and a partial emission apiece.
      isApplyingRef.current = true
      try {
        api?.forEachNode((node) => {
          if (
            node.isSelected() &&
            node.data &&
            !allowed.has(keyOf(node.data))
          ) {
            node.setSelected(false, false)
          }
        })
      } finally {
        isApplyingRef.current = false
      }
      void message.warning(
        `Selection is limited to ${maxSelection} items. Extra rows were deselected.`
      )
      onSelectionChange([...heldRows, ...trimmedPage])
      return
    }

    // Alongside isApplyingRef: a genuine click landing on the selection we
    // already hold is not worth re-emitting.
    if (hasSameKeys(selectedKeys, merged.map(keyOf))) return
    onSelectionChange(merged)
  }, [
    gridRef,
    keyOf,
    maxSelection,
    onSelectionChange,
    rows,
    selectedKeys,
    selectedRows,
  ])

  return { onSelectionChanged }
}

/**
 * A refetch of the same page is not a grid click: keys may stay ticked while
 * the row objects behind them are replaced (status, in-use, …). The owner
 * derives eligibility from those objects, so overlay the latest page —
 * snapshots refreshed, keys that left the page dropped — rather than treating
 * unchanged keys as an unchanged selection.
 *
 * `pageKey` omitted means placeholder data from another page: refresh
 * snapshots we can see, but do not record these keys as "the current page"
 * (that would make the real page look like a refetch and drop the previous
 * page's selection).
 *
 * Pure so the selection owner can apply it against its own state. Associated
 * VCNs prunes after refetch the same way; the grid hook must not push the
 * result up through an effect.
 */
export function reconcilePageSelection<TRow>({
  rows,
  selectedRows,
  keyOf,
  pageKey,
  prevPageKey,
  prevRowKeys,
}: ReconcilePageSelectionInput<TRow>): ReconcilePageSelectionResult<TRow> {
  const rowByKey = indexRows(rows, keyOf)
  const currentRowKeys = new Set(rowByKey.keys())

  if (pageKey === undefined) {
    return {
      selectedRows: reconcileSelectedRows(selectedRows, rowByKey, keyOf),
      pageKey: prevPageKey,
      rowKeys: prevRowKeys,
    }
  }

  const pageChanged = pageKey !== prevPageKey
  const dropKeys = pageChanged
    ? undefined
    : new Set(Array.from(prevRowKeys).filter((key) => !currentRowKeys.has(key)))

  return {
    selectedRows: reconcileSelectedRows(
      selectedRows,
      rowByKey,
      keyOf,
      dropKeys
    ),
    pageKey,
    rowKeys: currentRowKeys,
  }
}
